// frontend/src/contexts/AuthContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import type { ReactNode } from "react";
import * as api from "../services/api";
import type { Artist, BusinessType, Organization, RegisterData, User } from "../types";

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  businessType: BusinessType;
  /** @deprecated Utiliser organization + user */
  artist: Artist | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDemoMode: boolean;
  platformCapabilities: {
    whatsapp: boolean;
    email: boolean;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const toLegacyArtist = (
  user: User | null,
  organization: Organization | null
): Artist | null => {
  if (!user || !organization) return null;

  return {
    id: organization.id,
    name: user.name,
    email: user.email,
    specialty: organization.specialty,
    isAvailable: organization.isAvailable,
    avgTimePerTattoo: organization.avgTimePerClient,
    avgTimePerClient: organization.avgTimePerClient,
    businessType: organization.businessType,
    phone: organization.phone,
    createdAt: organization.createdAt,
    updatedAt: organization.createdAt,
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<{
    isDemoMode: boolean;
    capabilities: { whatsapp: boolean; email: boolean };
  }>({
    isDemoMode: false,
    capabilities: { whatsapp: false, email: false },
  });

  const artist = useMemo(
    () => toLegacyArtist(user, organization),
    [user, organization]
  );

  const businessType = organization?.businessType ?? "OTHER";

  const logout = (): void => {
    setToken(null);
    setUser(null);
    setOrganization(null);
    localStorage.removeItem("token");
    api.setAuthToken(null);
  };

  useEffect(() => {
    let cancelled = false;

    const init = async (): Promise<void> => {
      try {
        const platformConfig = await api.getConfig();
        if (!cancelled) setConfig(platformConfig);
      } catch (err: unknown) {
        const axiosErr = err as { message?: string };
        console.error("❌ Failed to fetch platform config:", axiosErr.message);
      }

      const stored = localStorage.getItem("token");
      if (stored) {
        api.setAuthToken(stored);
        try {
          const session = await api.getCurrentSession();
          if (!cancelled) {
            setToken(stored);
            setUser(session.user);
            setOrganization(session.organization);
          }
        } catch {
          if (!cancelled) logout();
        }
      }
      if (!cancelled) setIsLoading(false);
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const result = await api.login(email, password);
    api.setAuthToken(result.token);
    setToken(result.token);
    setUser(result.user);
    setOrganization(result.organization);
  };

  const register = async (data: RegisterData) => {
    const result = await api.register(data);
    api.setAuthToken(result.token);
    setToken(result.token);
    setUser(result.user);
    setOrganization(result.organization);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        businessType,
        artist,
        token,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isLoading,
        isDemoMode: config.isDemoMode,
        platformCapabilities: config.capabilities,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé dans AuthProvider");
  }
  return context;
};
